import { FileText } from 'lucide-react';

import { getAttachmentsByIdAccessUrl } from '@/api/generated/attachments/attachments';
import {
  type GetAssistantSessionsBySessionIdMessages200MessagesItemCitationsAnyOfOneOfThree as AttachmentCitation,
  type GetAssistantSessionsBySessionIdMessages200MessagesItemCitationsAnyOfOneOf as EmailCitation,
  GetAttachmentsByIdAccessUrlDisposition,
} from '@/api/generated/lucioAPI.schemas';
import { endSessionIfSignedInElsewhere } from '@/api/mutator';
import arrowIcon from '@/assets/icons/arrowIconWhite.svg';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { getAuthHeaders } from '@/config/auth/deviceToken';
import { apiBaseUrl } from '@/utils/apiBaseUrl';
import { sanitizeHtmlForDisplay } from '@/utils/emailContentDisplayUtils';
import { useUserLocaleAndTimezone } from '@/utils/hooks/useUserLocaleAndTimezone';

type Citation = AttachmentCitation | EmailCitation;

interface CitationButtonProps {
  citation: Citation;
  citationNumber: string;
  onViewMail?: ((messageId: string) => void) | undefined;
}

export function CitationButton({ citation, citationNumber, onViewMail }: CitationButtonProps) {
  if (citation.kind === 'attachment') {
    return <AttachmentCitationButton citation={citation} citationNumber={citationNumber} />;
  }
  return (
    <EmailCitationButton
      citation={citation as EmailCitation}
      citationNumber={citationNumber}
      onViewMail={onViewMail}
    />
  );
}

function EmailCitationButton({
  citation,
  citationNumber,
  onViewMail,
}: {
  citation: EmailCitation;
  citationNumber: string;
  onViewMail?: ((messageId: string) => void) | undefined;
}) {
  const sanitizedHtml = sanitizeHtmlForDisplay(citation.summary);
  const posthog = usePostHog();
  const { locale, timeZone } = useUserLocaleAndTimezone();

  const formattedDate = new Date(citation.receivedAt).toLocaleString(locale, {
    day: 'numeric',
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });

  const handleViewMail = () => {
    posthog.capture(ANALYTICS_EVENTS.ASSISTANT_CITATION_CLICKED, {
      citationNumber,
      kind: 'email',
      messageId: citation.messageId,
    });
    onViewMail?.(citation.messageId);
  };

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="citation-button text-primary bg-elevated-hover hover:bg-elevated-hover mx-0.5 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-md px-0.5 text-[10px] font-medium transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {citationNumber}
          </button>
        </TooltipTrigger>
        <TooltipContent
          align="start"
          className="border-subtle bg-popover flex max-h-[min(400px,calc(100vh-120px))] w-70 max-w-[min(280px,calc(100vw-32px))] flex-col overflow-hidden rounded-md border-[0.5px] p-0 wrap-break-word shadow-lg"
          side="top"
          sideOffset={8}
        >
          <div className="flex items-center gap-3 p-3">
            <div className="bg-info flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
              {citation.from.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-secondary truncate font-['Inter'] text-[12px] leading-[170%] font-semibold tracking-normal">
                {citation.from.name}
              </p>
              <p className="text-primary line-clamp-2 font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal">
                {citation.subject}
              </p>
              <p className="text-tertiary text-[10px] leading-[160%]">{formattedDate}</p>
            </div>
          </div>
          <div className="border-border-subtle mb-1 border-t"></div>

          <div
            className="text-foreground max-h-[min(120px,calc(100vh-280px))] min-h-0 flex-1 overflow-y-auto px-4 py-1 text-xs leading-5 wrap-break-word"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
          <div className="px-4 pt-1 pb-3">
            <Button
              className="bg-primary hover:bg-opacity-90 text-primary-foreground ml-auto flex h-6 w-19.5 items-center justify-center gap-1 rounded-md px-2 text-[10px] font-medium transition-colors"
              onClick={handleViewMail}
            >
              View Mail
              <img alt="Arrow" className="icon-on-primary" src={arrowIcon} />
            </Button>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function isPdfMimeOrName(citation: AttachmentCitation): boolean {
  return (
    citation.mimeType === 'application/pdf' || citation.fileName.toLowerCase().endsWith('.pdf')
  );
}

/**
 * Resolve the bytes URL for an attachment citation.
 *  - CHAT_UPLOAD → mint a short-lived Azure SAS URL
 *  - EMAIL       → build the auth-gated stream-on-demand route URL
 *
 * The PDF viewer's `loadDocumentSource` knows to attach the MSAL bearer token
 * when the URL points at our API.
 */
async function resolveAttachmentUrl(
  citation: AttachmentCitation,
  disposition: 'inline' | 'attachment',
): Promise<string> {
  if (citation.sourceType === 'CHAT_UPLOAD') {
    const response = await getAttachmentsByIdAccessUrl(citation.attachmentId, {
      disposition: GetAttachmentsByIdAccessUrlDisposition[disposition],
    });
    return response.url;
  }
  if (!citation.emailAttachmentId) {
    throw new Error('Email-source attachment is missing graph identifiers.');
  }
  const [graphMessageId, graphAttachmentId] = citation.emailAttachmentId.split('::');
  if (!graphMessageId || !graphAttachmentId) {
    throw new Error('Malformed email attachment identifier.');
  }
  return (
    `${apiBaseUrl()}/attachments/email/` +
    `${encodeURIComponent(graphMessageId)}/${encodeURIComponent(graphAttachmentId)}/stream` +
    `?disposition=${disposition}`
  );
}

async function downloadEmailAttachmentBlob(url: string, fileName: string): Promise<void> {
  const res = await fetch(url, { headers: (await getAuthHeaders()) ?? {} });
  if (!res.ok) {
    await endSessionIfSignedInElsewhere(res);
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.append(link);
  link.click();
  link.remove();
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

function AttachmentCitationButton({
  citation,
  citationNumber,
}: {
  citation: AttachmentCitation;
  citationNumber: string;
}) {
  const posthog = usePostHog();
  const isPdf = isPdfMimeOrName(citation);

  const sourceId =
    citation.sourceType === 'EMAIL' ? (citation.emailAttachmentId ?? '') : citation.attachmentId;

  const trigger = async () => {
    posthog.capture(ANALYTICS_EVENTS.ASSISTANT_CITATION_CLICKED, {
      citationNumber,
      fileName: citation.fileName,
      kind: 'attachment',
      sourceId,
      sourceType: citation.sourceType,
    });

    try {
      if (isPdf) {
        const inlineUrl = await resolveAttachmentUrl(citation, 'inline');
        const dialogParams = new URLSearchParams();
        dialogParams.set('page', 'pdf-viewer');
        dialogParams.set('url', inlineUrl);
        dialogParams.set('fileName', citation.fileName);
        if (typeof citation.page === 'number' && Number.isFinite(citation.page)) {
          dialogParams.set('pageNum', citation.page.toString());
        }
        if (citation.quote) {
          dialogParams.set('search', citation.quote);
        }
        const dialogUrl = `${window.location.origin}/taskpane.html?${dialogParams.toString()}`;
        Office.context.ui.displayDialogAsync(
          dialogUrl,
          { displayInIframe: false, height: 80, width: 80 },
          () => undefined,
        );
        return;
      }

      // Non-PDF → download
      if (citation.sourceType === 'CHAT_UPLOAD') {
        const url = await resolveAttachmentUrl(citation, 'attachment');
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener noreferrer';
        document.body.append(link);
        link.click();
        link.remove();
      } else {
        const url = await resolveAttachmentUrl(citation, 'attachment');
        await downloadEmailAttachmentBlob(url, citation.fileName);
      }
    } catch (error) {
      console.error('Failed to open citation source:', error);
    }
  };

  const ctaLabel = isPdf ? 'View document' : 'Download';

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="citation-button text-primary bg-elevated-hover hover:bg-elevated-hover mx-0.5 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-md px-0.5 text-[10px] font-medium transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {citationNumber}
          </button>
        </TooltipTrigger>
        <TooltipContent
          align="start"
          className="border-subtle bg-popover flex max-h-[min(400px,calc(100vh-120px))] w-70 max-w-[min(280px,calc(100vw-32px))] flex-col overflow-hidden rounded-md border-[0.5px] p-0 wrap-break-word shadow-lg"
          side="top"
          sideOffset={8}
        >
          <div className="flex items-center gap-3 p-3">
            <div className="bg-success flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-secondary truncate font-['Inter'] text-[12px] leading-[170%] font-semibold tracking-normal"
                title={citation.fileName}
              >
                {citation.fileName}
              </p>
              {typeof citation.page === 'number' && (
                <p className="text-tertiary text-[10px] leading-[160%]">Page {citation.page}</p>
              )}
              {citation.parentEmail && (
                <p
                  className="text-tertiary line-clamp-1 text-[10px] leading-[160%]"
                  title={`From "${citation.parentEmail.subject}" — ${citation.parentEmail.from.name}`}
                >
                  From &ldquo;{citation.parentEmail.subject}&rdquo;
                </p>
              )}
            </div>
          </div>
          <div className="border-border-subtle mb-1 border-t"></div>

          {citation.quote ? (
            <blockquote className="border-l-primary text-primary mx-4 my-2 max-h-[min(120px,calc(100vh-280px))] overflow-y-auto border-l-2 px-2 text-xs leading-5 wrap-break-word italic">
              &ldquo;{citation.quote}&rdquo;
            </blockquote>
          ) : (
            <p className="text-tertiary px-4 py-2 text-[11px] italic">
              No verifiable quote was provided for this citation.
            </p>
          )}

          <div className="px-4 pt-1 pb-3">
            <Button
              className="bg-primary hover:bg-opacity-90 text-primary-foreground ml-auto flex h-6 items-center justify-center gap-1 rounded-md px-2 text-[10px] font-medium transition-colors"
              onClick={() => void trigger()}
            >
              {ctaLabel}
              <img alt="Arrow" className="icon-on-primary" src={arrowIcon} />
            </Button>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
